import {
	LoginBody,
	LoginResponse,
	PAGES,
	RegisterPasswordType,
	SocietyUserVar,
} from "@dune/types";
import { getErrorMessage } from "@dune/vars";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuthStore } from "../../stores/auth.store";
import { checkBrokerAccess } from "../../utils/brokerAccessUtils";
import { attachToken, privateAxios, publicAxios } from "../axios";
import { getMe } from "../queries/users.queries";
import { societiesQueryKeys } from "../query-keys/societies.query-keys";
import { usersQueryKeys } from "../query-keys/users.query-keys";

const loginUser = async (user: LoginBody) => {
	const { data } = await publicAxios.post<LoginResponse>("/users/login", user);
	return { response: data, email: user.email };
};

export const useLoginUser = () => {
	const navigate = useNavigate();

	return useMutation({
		mutationFn: loginUser,
		onSuccess: async ({ response, email }) => {
			if (response.AuthenticationResult?.AccessToken) {
				const state = useAuthStore.getState();
				try {
					state.login(
						response.AuthenticationResult.AccessToken,
						response.AuthenticationResult.RefreshToken,
						email,
					);
					attachToken(response.AuthenticationResult.AccessToken);

					const me = await getMe();

					if (me.role === "BROKER") {
						const accessCheck = await checkBrokerAccess();
						navigate(accessCheck.redirectTo);
					} else {
						navigate(PAGES.ROOT);
					}
				} catch {
					state.logout();
				}
			} else {
				toast("Echec lors de la connexion", {
					description: "Token d'accès manquant ou invalide",
				});
			}
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Echec lors de la connexion", {
				description: errorMessage,
			});
		},
	});
};

export const resetPassword = async (user: RegisterPasswordType) => {
	const { data } = await publicAxios.post("/users/reset-password", user);
	return data;
};

export const useResetPassword = () => {
	const navigate = useNavigate();

	return useMutation({
		mutationFn: resetPassword,
		onSuccess: () => {
			navigate(PAGES.LOGIN);
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Echec lors de l'envoi du changement de mot de passe", {
				description: errorMessage,
			});
		},
	});
};

export const forgetPassword = async (email: string) => {
	const { data } = await publicAxios.post("/users/forget-password", { email });
	return data;
};

export const useForgetPassword = () => {
	return useMutation({
		mutationFn: forgetPassword,
		onSuccess: () => {},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Echec lors de l'envoi du mail", {
				description: errorMessage,
			});
		},
	});
};

interface UpdateUserMutationProps {
	user: Partial<SocietyUserVar>;
}

const updateUser = async ({ user }: UpdateUserMutationProps) => {
	const { data } = await privateAxios.patch(`/users/${user.id}`, user);
	return data;
};

export const useUpdateUser = (societyId: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: updateUser,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.brokerUsers(societyId),
			});
			toast.success("Utilisateur mis à jour avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la mise à jour de l'utilisateur", {
				description: errorMessage,
			});
		},
	});
};

interface UpdateUserSignatureMutationProps {
	emailSignature?: string;
}

const updateUserSignature = async ({
	emailSignature,
}: UpdateUserSignatureMutationProps) => {
	const { data } = await privateAxios.patch("/users/me/signature", {
		emailSignature,
	});
	return data;
};

export const useUpdateUserSignature = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: updateUserSignature,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: usersQueryKeys.me(),
			});
			toast.success("Signature mise à jour avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la mise à jour de la signature", {
				description: errorMessage,
			});
		},
	});
};
